import React from 'react';
import { X, Lock, Shield, Database, Users } from 'lucide-react';

interface ConfidentialityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConfidentialityModal: React.FC<ConfidentialityModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-cyan-600" />
              <h2 className="text-2xl font-semibold text-gray-900">Confidentiality Assurance</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-6">
                At Prestige AI, we are dedicated to maintaining the highest standards of confidentiality, security, and privacy for your data. We utilize industry-leading tools and platforms to design AI-driven bots that enhance audit efficiency and quality, ensuring your information remains protected throughout the process.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-cyan-50 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Database className="h-6 w-6 text-cyan-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Data Handling and Storage</h3>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li>Secure data processing within our web application</li>
                    <li>30-day maximum temporary storage for debugging</li>
                    <li>Advanced encryption standards for data at rest</li>
                    <li>Strict access controls and permissions</li>
                    <li>ISO 27001 and SOC 2 compliant storage solutions</li>
                  </ul>
                </div>

                <div className="bg-cyan-50 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Lock className="h-6 w-6 text-cyan-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Data Privacy and Security</h3>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li>No data sharing or unauthorized disclosure</li>
                    <li>No AI model training using client data</li>
                    <li>Industry-standard security frameworks</li>
                    <li>Secure data center infrastructure</li>
                    <li>Regular security audits and updates</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Internal Policies and Procedures</h3>
                  <div className="flex items-start space-x-3">
                    <Users className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                    <div className="text-gray-600">
                      <p className="mb-2">We maintain comprehensive policies and procedures to ensure client data confidentiality:</p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Strict access control and monitoring</li>
                        <li>Regular employee training on data privacy</li>
                        <li>Continuous security protocol updates</li>
                        <li>Incident response procedures</li>
                      </ul>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Assurance</h3>
                  <p className="text-gray-600">
                    We are dedicated to providing a secure and trustworthy environment for all your auditing automation needs. Our commitment to data security and privacy is unwavering, and we continuously review and enhance our practices to meet the highest standards.
                  </p>
                </section>

                <div className="bg-gray-50 p-6 rounded-lg mt-6">
                  <p className="text-gray-600">
                    If you have any questions or concerns about our data handling practices, please contact us at{' '}
                    <a href="mailto:support@prestigeai.co.za" className="text-cyan-600 hover:text-cyan-700">
                      support@prestigeai.co.za
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfidentialityModal;